import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Edit, Trash2, ExternalLink, Save } from "lucide-react";
import { toast } from "sonner";
import ImageInput from "./ImageInput";

const EMPTY = { title: "", description: "", kind: "product", category: "", price: "", image_url: "", sale_url: "", active: true, checkout_enabled: false, cashback_pct_on_checkout: "", turbo_active: false, turbo_cashback_pct: "", manage_stock: false, stock_qty: "", min_stock: "1" };

export default function MerchantProducts({ company }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [showCatMgr, setShowCatMgr] = useState(false);

  const refresh = () => {
    api.get(`/products/owner/${company.id}`).then((r) => setItems(r.data));
    api.get(`/products/categories/${company.id}`).then((r) => setCategories(r.data || [])).catch(() => setCategories([]));
  };
  useEffect(() => { if (company?.id) refresh(); }, [company?.id]);

  if (company.plan !== "OURO") {
    return (
      <Card className="p-6 rounded-2xl border-amber-200 bg-amber-50">
        <p className="text-sm text-amber-900">
          <strong>Catálogo de Produtos & Serviços</strong> é exclusivo do plano <strong>OURO</strong>. Faça upgrade para liberar esse recurso e vender online com seu link de pagamento.
        </p>
      </Card>
    );
  }

  const openNew = () => { setForm(EMPTY); setEditing("NEW"); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p, price: p.price.toString() }); setEditing(p.id); };

  const save = async () => {
    setBusy(true);
    try {
      const { id: _id, created_at: _ca, ...rest } = form;
      const cb = parseFloat(rest.cashback_pct_on_checkout);
      const turbo = parseFloat(rest.turbo_cashback_pct);
      const stockQ = parseInt(rest.stock_qty, 10);
      const minSt = parseInt(rest.min_stock, 10);
      const payload = {
        ...rest,
        company_id: company.id,
        price: parseFloat(rest.price) || 0,
        cashback_pct_on_checkout: Number.isFinite(cb) ? cb : 0,
        turbo_active: !!rest.turbo_active,
        turbo_cashback_pct: Number.isFinite(turbo) ? turbo : 0,
        manage_stock: !!rest.manage_stock,
        stock_qty: Number.isFinite(stockQ) ? stockQ : 0,
        min_stock: Number.isFinite(minSt) ? minSt : 1,
        category: rest.category || "",
        image_url: rest.image_url || null,
        sale_url: rest.sale_url || null,
      };
      if (editing === "NEW") await api.post("/products", payload);
      else await api.patch(`/products/${editing}`, payload);
      toast.success("Salvo");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Falha ao salvar"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir?")) return;
    await api.delete(`/products/${id}`); toast.success("Excluído"); refresh();
  };

  const createCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    try {
      await api.post("/products/categories", { company_id: company.id, name });
      toast.success("Categoria criada");
      setNewCat("");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteCategory = async (id, name) => {
    if (!window.confirm(`Excluir categoria "${name}"?`)) return;
    try { await api.delete(`/products/categories/${id}`); toast.success("Categoria excluída"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-display text-xl font-semibold">Produtos & Serviços de {company.name}</h3>
          <p className="text-sm text-stone-500">Adicione itens com fotos, preços e links de venda. Aparecem na página pública.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowCatMgr((v) => !v)} className="rounded-xl" data-testid="toggle-cat-mgr">
            Categorias ({categories.length})
          </Button>
          <Button onClick={openNew} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="add-product-btn"><Plus size={14} className="mr-1.5"/> Novo item</Button>
        </div>
      </div>

      {showCatMgr && (
        <Card className="p-4 rounded-2xl border-amber-200 bg-amber-50/40 mb-5" data-testid="cat-manager">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createCategory()} placeholder="Nova categoria (ex.: Bebidas, Massas, Cortes…)" className="rounded-xl flex-1 min-w-[200px] bg-white" data-testid="cat-input"/>
            <Button onClick={createCategory} disabled={!newCat.trim()} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="cat-create"><Plus size={14} className="mr-1"/>Criar</Button>
          </div>
          {categories.length === 0 ? (
            <p className="text-xs text-stone-500">Nenhuma categoria. Crie a primeira pra organizar seu cardápio.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <span key={c.id} className="inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full bg-white border border-amber-200 text-sm" data-testid={`cat-pill-${c.id}`}>
                  {c.name}
                  <button onClick={() => deleteCategory(c.id, c.name)} className="w-5 h-5 inline-flex items-center justify-center rounded-full hover:bg-red-100 text-red-500" aria-label={`Remover ${c.name}`} data-testid={`cat-del-${c.id}`}>
                    <Trash2 size={12}/>
                  </button>
                </span>
              ))}
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-4">
        {items.length === 0 && <p className="text-sm text-stone-500 col-span-full">Nenhum item ainda.</p>}
        {items.map((p) => {
          const lowStock = p.manage_stock && (Number(p.stock_qty || 0) < Number(p.min_stock || 1));
          return (
          <Card key={p.id} className={`overflow-hidden rounded-xl sm:rounded-2xl border ${p.active ? (lowStock ? "border-red-200" : "border-stone-200") : "border-red-200 opacity-60"}`} data-testid={`merchant-product-${p.id}`}>
            {p.image_url && <img src={p.image_url} alt={p.title} className="w-full aspect-square sm:aspect-[4/3] object-cover"/>}
            <div className="p-2 sm:p-3">
              <div className="flex items-center gap-1 text-[9px] sm:text-[10px] mb-1 flex-wrap">
                <span className="font-bold uppercase tracking-wider px-1 sm:px-1.5 py-0.5 rounded bg-stone-100 text-stone-700">{p.kind === "service" ? "Serv" : "Prod"}</span>
                {p.turbo_active && p.turbo_cashback_pct > 0 && (
                  <span className="font-bold uppercase tracking-wider px-1 sm:px-1.5 py-0.5 rounded bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-sm" data-testid={`turbo-badge-${p.id}`}>⚡+{p.turbo_cashback_pct}%</span>
                )}
                {lowStock && <span className="font-bold uppercase tracking-wider px-1 sm:px-1.5 py-0.5 rounded bg-red-100 text-red-700">Sem est.</span>}
                {!p.active && <span className="text-red-600">Inativo</span>}
              </div>
              <h4 className="font-display font-semibold text-stone-900 leading-tight text-xs sm:text-sm line-clamp-2">{p.title}</h4>
              {p.manage_stock && (
                <div className="text-[10px] text-stone-500 mt-1">Estoque: <strong className={lowStock ? "text-red-600" : "text-stone-700"}>{p.stock_qty || 0}</strong></div>
              )}
              <div className="flex items-center justify-between mt-1.5 sm:mt-2 gap-1">
                <span className="font-display text-sm sm:text-lg font-bold text-blue-950">R$ {(p.price || 0).toFixed(2)}</span>
                <div className="flex gap-0.5 sm:gap-1">
                  {p.sale_url && <a href={p.sale_url} target="_blank" rel="noreferrer" className="text-stone-500 hover:text-blue-900 p-1"><ExternalLink size={12}/></a>}
                  <button onClick={() => openEdit(p)} className="text-stone-500 hover:text-stone-900 p-1" data-testid={`edit-prod-${p.id}`}><Edit size={12}/></button>
                  <button onClick={() => remove(p.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={12}/></button>
                </div>
              </div>
            </div>
          </Card>
          );
        })}
      </div>

      {editing && (
        <Dialog open onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent className="max-w-xl rounded-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader><DialogTitle className="font-display text-xl sm:text-2xl">{editing === "NEW" ? "Novo item" : "Editar item"}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="sm:col-span-2"><Label>Título</Label><Input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="rounded-xl mt-1" data-testid="pf-title"/></div>
              <div className="sm:col-span-2"><Label>Descrição</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="rounded-xl mt-1"/></div>
              <div>
                <Label>Tipo</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({...form, kind: v})}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="pf-kind"><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="product">Produto</SelectItem><SelectItem value="service">Serviço</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoria</Label>
                <Select value={form.category || "__none__"} onValueChange={(v) => setForm({...form, category: v === "__none__" ? "" : v})}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="pf-category"><SelectValue placeholder="Selecione..."/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Sem categoria —</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {categories.length === 0 && (
                  <p className="text-[11px] text-amber-700 mt-1">Nenhuma categoria criada ainda. Use o botão <strong>Categorias</strong> acima para criar.</p>
                )}
              </div>
              <div><Label>Preço (R$)</Label><Input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} className="rounded-xl mt-1" data-testid="pf-price"/></div>
              <div className="flex items-center gap-2 mt-6"><Switch checked={!!form.active} onCheckedChange={(v) => setForm({...form, active: v})}/><Label>Ativo</Label></div>
              {form.kind === "product" && (
                <div className="sm:col-span-2 p-3 rounded-xl border border-blue-200 bg-blue-50/40">
                  <div className="flex items-center gap-2 mb-2">
                    <Switch checked={!!form.manage_stock} onCheckedChange={(v) => setForm({...form, manage_stock: v})} data-testid="pf-manage-stock"/>
                    <Label className="font-semibold text-blue-900">Controlar estoque deste produto</Label>
                  </div>
                  <p className="text-[11px] text-blue-800 mb-2">Quando ligado, o produto fica indisponível se o estoque cair abaixo da quantidade mínima.</p>
                  {form.manage_stock && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Estoque atual</Label>
                        <Input type="number" min="0" step="1" value={form.stock_qty} onChange={(e) => setForm({...form, stock_qty: e.target.value})} className="rounded-xl mt-1 bg-white" placeholder="Ex: 50" data-testid="pf-stock-qty"/>
                      </div>
                      <div>
                        <Label className="text-xs">Mínimo p/ vender</Label>
                        <Input type="number" min="1" step="1" value={form.min_stock} onChange={(e) => setForm({...form, min_stock: e.target.value})} className="rounded-xl mt-1 bg-white" placeholder="Ex: 1" data-testid="pf-min-stock"/>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="sm:col-span-2 p-3 rounded-xl border border-emerald-200 bg-emerald-50">
                <div className="flex items-center gap-2 mb-2">
                  <Switch checked={!!form.checkout_enabled} onCheckedChange={(v) => setForm({...form, checkout_enabled: v})} data-testid="pf-checkout-enabled"/>
                  <Label className="font-semibold text-emerald-900">Vender pelo Checkout MorarBem</Label>
                </div>
                <p className="text-[11px] text-emerald-800 mb-2">Cliente compra direto no app. Você recebe via PIX (chave configurada na empresa) menos a taxa da plataforma.</p>
                {form.checkout_enabled && (
                  <div>
                    <Label className="text-xs">Cashback BemCash desse produto (% sobre o valor pago em MP)</Label>
                    <Input type="number" step="0.1" min="0" max="100" value={form.cashback_pct_on_checkout} onChange={(e) => setForm({...form, cashback_pct_on_checkout: e.target.value})} className="rounded-xl mt-1" placeholder="Ex: 5" data-testid="pf-cb-checkout"/>
                  </div>
                )}
              </div>
              <div className="sm:col-span-2 p-3 rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50">
                <div className="flex items-center gap-2 mb-2">
                  <Switch checked={!!form.turbo_active} onCheckedChange={(v) => setForm({...form, turbo_active: v})} data-testid="pf-turbo-active"/>
                  <Label className="font-semibold text-amber-900 flex items-center gap-1">⚡ Turbo · cashback extra deste produto</Label>
                </div>
                <p className="text-[11px] text-amber-800 mb-2">
                  Adiciona % extra <strong>sobre o cashback do seu plano</strong> (ex: plano 5% + turbo 10% = cliente ganha 15%).
                  Esse extra é debitado da sua Carteira de Marketing. Se a carteira zerar, o Turbo é desligado automaticamente.
                </p>
                {form.turbo_active && (
                  <div>
                    <Label className="text-xs">Cashback Turbo (% extra)</Label>
                    <Input type="number" step="0.1" min="0" max="100" value={form.turbo_cashback_pct} onChange={(e) => setForm({...form, turbo_cashback_pct: e.target.value})} className="rounded-xl mt-1 bg-white" placeholder="Ex: 10" data-testid="pf-turbo-pct"/>
                  </div>
                )}
              </div>
              <div className="sm:col-span-2"><Label>Imagem (proporção 4:3 recomendada)</Label>
                <div className="mt-1"><ImageInput value={form.image_url} onChange={(v) => setForm({...form, image_url: v})} aspect="4/3" testid="pf-image"/></div>
              </div>
              <div className="sm:col-span-2"><Label>Link de venda externo (opcional, se NÃO usar checkout MorarBem)</Label><Input value={form.sale_url} onChange={(e) => setForm({...form, sale_url: e.target.value})} placeholder="WhatsApp, e-commerce externo..." className="rounded-xl mt-1" data-testid="pf-link"/></div>
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-950 text-white" data-testid="pf-save"><Save size={14} className="mr-1.5"/>{busy ? "Salvando..." : "Salvar"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
